import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { X, Upload, Video, Fuel, Gauge, PenLine, Eraser, Check, Camera, Loader2 } from "lucide-react";
import { api } from "../../lib/api";

/**
 * PickupHandoffModal — manager-driven in-person vehicle release flow.
 * Runs on the manager's device (phone/tablet) at pickup time.
 *
 * Mobile hardening (Feb 2026):
 *   • Per-upload `timeout: 600000` (10 min) and `maxContentLength: Infinity`
 *     — the axios default of 30s was killing every video upload over 4G/5G
 *     with a generic "Upload failed" toast.
 *   • Live upload progress (`onUploadProgress`) rendered inline so the
 *     manager sees bytes moving instead of a frozen button.
 *   • Removed `capture="environment"` from the video input — iOS Safari's
 *     handling of `capture` on video is buggy and often prevents the picker
 *     from opening at all. `accept="video/*"` alone lets iPhone show its
 *     built-in "Take Video / Choose from Library" sheet reliably.
 */
export default function PickupHandoffModal({ rental, onClose, onDone }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadLabel, setUploadLabel] = useState("");
  const [mileage, setMileage] = useState("");
  const [fuel, setFuel] = useState("F");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sigData, setSigData] = useState("");
  const [dlFront, setDlFront] = useState(null);
  const [dlBack, setDlBack] = useState(null);
  // Multi-clip walk-around — one file per angle. Each slot uploads
  // independently so a bad connection only kills the current angle.
  const [clips, setClips] = useState({}); // { front: {file_id, angle}, driver: {...}, ... }

  // Shared axios config for every upload here — 10-minute ceiling + progress.
  const uploadCfg = (label) => ({
    timeout: 10 * 60 * 1000,
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
    onUploadProgress: (e) => {
      if (!e.total) return;
      const pct = Math.round((e.loaded / e.total) * 100);
      setUploadPct(pct);
      setUploadLabel(`${label} · ${pct}%`);
    },
  });

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true); setUploadPct(0); setUploadLabel(`Photo (${(file.size/1024/1024).toFixed(1)} MB)`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/rentals/${rental.id}/pickup-photo`, fd,
        uploadCfg(`Photo (${(file.size/1024/1024).toFixed(1)} MB)`),
      );
      setPhotos((p) => [...p, { id: data.file_id, url: data.url }]);
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Upload failed — check your connection and try again");
    } finally {
      setUploading(false); setUploadPct(0); setUploadLabel("");
    }
  };

  const uploadDLSide = async (side, file) => {
    if (!file) return;
    setUploading(true); setUploadPct(0); setUploadLabel(`DL ${side}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/rentals/${rental.id}/pickup-dl?side=${side}`,
        fd,
        uploadCfg(`DL ${side}`),
      );
      if (side === "front") setDlFront({ id: data.file_id, url: data.url });
      else setDlBack({ id: data.file_id, url: data.url });
      toast.success(`Driver's license ${side} scanned`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Scan failed");
    } finally {
      setUploading(false); setUploadPct(0); setUploadLabel("");
    }
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    const mb = file.size / 1024 / 1024;
    if (mb > 1024) {
      toast.error(`Video is ${mb.toFixed(0)} MB — please trim to under 1 GB or record a shorter clip.`);
      return;
    }
    setUploading(true); setUploadPct(0); setUploadLabel(`Video (${mb.toFixed(0)} MB)`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(
        `/admin/bookings/${rental.id}/inspection-video`, fd,
        uploadCfg(`Video (${mb.toFixed(0)} MB)`),
      );
      toast.success("Inspection video uploaded");
    } catch (e) {
      const msg = e?.response?.data?.detail
        || (e?.code === "ECONNABORTED" ? "Upload timed out — try a shorter clip or better connection" : null)
        || e?.message
        || "Video upload failed";
      toast.error(msg);
    } finally {
      setUploading(false); setUploadPct(0); setUploadLabel("");
    }
  };

  // Multi-clip walk-around: one 15-second-ish clip per angle. Every clip
  // uploads via its own request so failures are localised to that angle.
  const uploadClip = async (angle, file) => {
    if (!file) return;
    const mb = file.size / 1024 / 1024;
    if (mb > 250) {
      toast.error(`${angle} clip is ${mb.toFixed(0)} MB — keep clips under 250 MB (try 720p or shorter).`);
      return;
    }
    setUploading(true); setUploadPct(0);
    setUploadLabel(`${angle[0].toUpperCase() + angle.slice(1)} clip (${mb.toFixed(0)} MB)`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/bookings/${rental.id}/inspection-clip?angle=${angle}`, fd,
        uploadCfg(`${angle} clip`),
      );
      setClips((prev) => ({ ...prev, [angle]: { file_id: data.file_id, angle } }));
      toast.success(`${angle[0].toUpperCase() + angle.slice(1)} clip saved`);
    } catch (e) {
      const msg = e?.response?.data?.detail
        || (e?.code === "ECONNABORTED" ? "Clip upload timed out — try a shorter recording" : null)
        || e?.message
        || `${angle} clip upload failed`;
      toast.error(msg);
    } finally {
      setUploading(false); setUploadPct(0); setUploadLabel("");
    }
  };

  const submit = async () => {
    if (!mileage || Number(mileage) < 0) return toast.error("Enter the current mileage");
    if (!sigData) return toast.error("Get the customer's signature to release the vehicle");
    setSubmitting(true);
    try {
      await api.post(`/admin/rentals/${rental.id}/handoff`, {
        mileage: Number(mileage),
        fuel_level: fuel,
        inspection_notes: notes.trim() || null,
        pickup_signature_data: sigData,
      });
      toast.success("Rental released — status: active");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Handoff failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-3 overflow-y-auto"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        data-testid="pickup-handoff-modal"
        className="w-full max-w-2xl bg-[#0B1120] rounded-3xl border border-cyan/40 shadow-2xl p-5 sm:p-6 space-y-5 my-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="label-caps text-cyan text-[10px]">Pickup handoff</div>
            <div className="font-serif text-2xl leading-tight">Release vehicle to customer</div>
            <div className="text-xs text-white/60 mt-1">
              {rental.vehicle_name} · {rental.full_name}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white text-2xl leading-none px-2">×</button>
        </div>

        {/* Photos */}
        <section>
          <div className="label-caps text-cyan text-[10px] mb-2">1 · Pre-rental photos</div>
          <div className="grid grid-cols-3 gap-2 mb-2">
            {photos.map((p) => (
              <img key={p.id} src={p.url} alt="Pickup"
                className="aspect-square object-cover rounded-lg border border-cyan/30" />
            ))}
            <label className={`aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition ${
              uploading ? "border-white/10 text-white/40" : "border-cyan/40 text-cyan hover:bg-cyan/5"
            }`}>
              <Camera size={20} />
              <span className="text-[10px] label-caps mt-1">{uploading ? "…" : "Add"}</span>
              <input type="file" accept="image/*" capture="environment"
                data-testid="pickup-photo-input"
                onChange={(e) => uploadPhoto(e.target.files?.[0])}
                disabled={uploading}
                className="hidden" />
            </label>
          </div>
        </section>

        {/* Driver's license rescan — mandatory when the customer's original
            uploads are missing or blurry. Manager takes fresh photos on
            their phone right at pickup. */}
        <section>
          <div className="label-caps text-cyan text-[10px] mb-2">1b · Scan driver's license (front & back)</div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { side: "front", data: dlFront, label: "Front" },
              { side: "back", data: dlBack, label: "Back" },
            ].map(({ side, data, label }) => (
              <label key={side} className={`aspect-[3/2] rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition overflow-hidden ${
                uploading ? "border-white/10 text-white/40" : "border-cyan/40 text-cyan hover:bg-cyan/5"
              }`}>
                {data ? (
                  <img src={data.url} alt={`DL ${side}`} className="w-full h-full object-cover" />
                ) : (
                  <>
                    <Camera size={20} />
                    <span className="text-[10px] label-caps mt-1">DL {label}</span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  data-testid={`pickup-dl-${side}-input`}
                  onChange={(e) => uploadDLSide(side, e.target.files?.[0])}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
            ))}
          </div>
          <p className="text-[10px] text-white/50 mt-1">
            Rescan replaces the customer's original DL upload on file. Optional if their photos are already clean.
          </p>
        </section>

        {/* Multi-clip walk-around — 5 short clips beat one giant video.
            Each slot uploads independently so if one fails the manager
            only re-shoots that angle. Backend caps each clip at 250 MB. */}
        <section>
          <div className="label-caps text-cyan text-[10px] mb-2">2 · Walk-around clips (up to 5 · one per angle)</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-1">
            {[
              { key: "front",     label: "Front" },
              { key: "driver",    label: "Driver" },
              { key: "rear",      label: "Rear" },
              { key: "passenger", label: "Passenger" },
              { key: "interior",  label: "Interior" },
            ].map(({ key, label }) => {
              const clip = clips[key];
              return (
                <label
                  key={key}
                  className={`aspect-[4/5] rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition text-center relative overflow-hidden ${
                    clip
                      ? "border-lime-400/60 bg-lime-400/10 text-lime-200"
                      : uploading
                        ? "border-white/10 text-white/40"
                        : "border-cyan/40 text-cyan hover:bg-cyan/5"
                  }`}
                >
                  {clip ? (
                    <>
                      <Check size={22} />
                      <span className="text-[10px] label-caps mt-1">{label}</span>
                      <span className="text-[9px] text-white/50">saved</span>
                    </>
                  ) : (
                    <>
                      <Video size={20} />
                      <span className="text-[10px] label-caps mt-1">{label}</span>
                      <span className="text-[9px] text-white/50">tap to add</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="video/*"
                    data-testid={`pickup-clip-${key}-input`}
                    onChange={(e) => uploadClip(key, e.target.files?.[0])}
                    disabled={uploading}
                    className="hidden"
                  />
                </label>
              );
            })}
          </div>
          <p className="text-[10px] text-white/50">
            Aim for 15-second clips per angle. iPhone: tap and choose <span className="text-white/80">Take Video</span> or <span className="text-white/80">Photo Library</span>. Small clips beat one giant video — uploads finish in seconds.
          </p>
        </section>

        {/* Live upload progress — replaces the frozen "Uploading…" button.
            Shows bytes moving so the manager knows something's happening. */}
        {uploading && uploadLabel && (
          <div data-testid="pickup-upload-progress" className="rounded-xl border border-cyan/30 bg-cyan/[0.05] p-3">
            <div className="flex items-center justify-between text-[11px] text-cyan mb-1.5">
              <span className="inline-flex items-center gap-1.5">
                <Loader2 size={11} className="animate-spin" /> Uploading {uploadLabel}
              </span>
              <span className="font-mono">{uploadPct}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-cyan transition-all duration-150"
                style={{ width: `${Math.max(2, uploadPct)}%` }} />
            </div>
          </div>
        )}

        {/* Mileage + Fuel */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <div className="label-caps text-cyan text-[10px] mb-1.5 flex items-center gap-1">
              <Gauge size={11} /> 3 · Odometer (miles)
            </div>
            <input type="number" min="0" inputMode="numeric"
              data-testid="pickup-mileage"
              value={mileage}
              onChange={(e) => setMileage(e.target.value)}
              placeholder="e.g. 42350"
              style={{ background: "#000", color: "#FFFFFF" }}
              className="w-full font-mono text-xl font-bold border-2 border-cyan focus:outline-none focus:ring-2 focus:ring-cyan/50 rounded-lg px-4 py-3 text-center" />
          </label>
          <div>
            <div className="label-caps text-cyan text-[10px] mb-1.5 flex items-center gap-1">
              <Fuel size={11} /> 4 · Fuel level
            </div>
            <div className="grid grid-cols-5 gap-1">
              {["E", "1/4", "1/2", "3/4", "F"].map((f) => (
                <button
                  key={f}
                  type="button"
                  data-testid={`pickup-fuel-${f.replace("/", "-")}`}
                  onClick={() => setFuel(f)}
                  className={`py-2.5 rounded-lg text-sm font-semibold border transition ${
                    fuel === f
                      ? "bg-cyan text-midnight border-cyan"
                      : "border-white/10 text-white/60 hover:border-cyan/40"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Notes */}
        <label className="block">
          <div className="label-caps text-cyan text-[10px] mb-1.5">5 · Inspection notes (optional)</div>
          <textarea rows={2} value={notes}
            data-testid="pickup-notes"
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Pre-existing scratches, dents, cosmetic notes…"
            style={{ background: "#000", color: "#FFFFFF" }}
            className="w-full border-2 border-white/15 focus:border-cyan focus:outline-none rounded-lg px-3 py-2 text-sm" />
        </label>

        {/* Second signature */}
        <section>
          <div className="label-caps text-cyan text-[10px] mb-2">6 · Customer signature (pickup)</div>
          <MiniSigPad value={sigData} onChange={setSigData} />
          <p className="text-[10px] text-white/50 mt-1">
            Hand the device to the customer — they sign to confirm the mileage, fuel, and photos above.
          </p>
        </section>

        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose}
            className="flex-1 py-3 rounded-full text-xs label-caps border border-white/12 text-white/70 hover:border-white/30">
            Cancel
          </button>
          <button type="submit"
            disabled={submitting || !mileage || !sigData}
            data-testid="pickup-handoff-submit"
            className="flex-[2] py-3 rounded-full text-xs label-caps bg-cyan text-midnight font-semibold hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5">
            <Check size={13} /> {submitting ? "Releasing…" : "Release vehicle · mark active"}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Compact black-canvas / white-ink signature pad — same principle as the
 * customer contract pad: guaranteed visible on any mobile screen.
 */
function MiniSigPad({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(!!value);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, c.width, c.height);
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return {
      x: (t.clientX - rect.left) * (c.width / rect.width),
      y: (t.clientY - rect.top) * (c.height / rect.height),
    };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 3.5; ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    setHasInk(true);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = useCallback(() => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#000000"; ctx.fillRect(0, 0, c.width, c.height);
    setHasInk(false);
    onChange("");
  }, [onChange]);

  return (
    <div className="rounded-xl border-2 border-cyan/50 overflow-hidden relative" style={{ background: "#000" }}>
      <canvas
        ref={canvasRef}
        width={720}
        height={160}
        data-testid="pickup-signature-pad"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="w-full h-32 touch-none block cursor-crosshair"
        style={{ background: "#000", display: "block" }}
      />
      {hasInk && (
        <button type="button" onClick={clear}
          data-testid="pickup-signature-clear"
          className="absolute top-1.5 right-1.5 text-[10px] label-caps text-white/70 bg-black/40 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
          <Eraser size={9} /> Clear
        </button>
      )}
    </div>
  );
}
